import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Table, Typography } from "antd";
import Stack from "components/common/Stack";
import formatDate from "utils/formatDate";
import Toolbar from "components/common/Toolbar";
import ROPEProjectFilters, {
  filterRopeLandingPageData
} from "./RopeLandingPageFilters";
import displayErrorNotification from "utils/displayErrorNotification";
import { trackComponent } from "telemetry/AppInsights";
import { ColumnProps } from "antd/lib/table";
import ButtonRow from "components/common/ButtonRow";
import SavePreferencesButton from "components/common/SavePreferencesButton";
import useLocalStorage from "utils/useLocalStorage";
import { convertRopeLandingPageData } from "utils/convert";
import sorters from "utils/sorters";
import RopeDetailsModal from "./RopeDetailsModal";
import {
  RopeFlagDynamics,
  RopeLandingPageData,
  RopeStatusImportable
} from "../../types/Rope";
import { getRopeLandingPageDataDto } from "../../api/ropeApi";
import { defaultRopeLandingPageFilterSettings } from "../../types/RopeLandingPagePreferences";
import { Link } from "react-router-dom";
import { useUser } from "../../auth/UserHooks";
import ProjectorLogo from "../common/ProjectorLogo";
import DynamicsLogo from "../common/DynamicsLogo";
import RopeImportConfirmModal from "./RopeImportConfirmModal";
import RopeUploadModal from "./RopeUploadModal";
import RopeViewLogModal from "./RopeViewLogModal";

const RopeLandingPage = () => {
  const user = useUser();
  const canImportRope = user.permissions.canImportRope;
  const canUploadRope = user.permissions.canUploadRope;
  const [ropeData, setRopeData] = useState<RopeLandingPageData[]>([]);
  const [filteredRopeData, setFilteredRopeData] = useState<
    RopeLandingPageData[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [filterSettings, setFilterSettings] = useLocalStorage(
    "RopeLandingPage_FilterSettings",
    defaultRopeLandingPageFilterSettings()
  );
  const [sortSettings, setSortSettings] = useLocalStorage<any>(
    "RopeLandingPage_SortSettings",
    {
      key: "projectName",
      order: "ascend"
    }
  );
  const searchRef = useRef<Input>();
  const [isDetailsModalHidden, setIsDetailsModalHidden] = useState(true);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isViewLogVisible, setIsViewLogVisible] = useState(false);
  const [
    selectedRecord,
    setSelectedRecord
  ] = useState<RopeLandingPageData | null>(null);

  useEffect(() => {
    refresh();
  }, []);

  const handleDetailsOpenModal = record => {
    setIsDetailsModalHidden(!isDetailsModalHidden);
    setSelectedRecord(record);
  };

  const openInNewTab = url => {
    window.open(url, "_blank", "noopener noreferrer");
  };

  let columns: ColumnProps<RopeLandingPageData>[] = [
    {
      title: "Project Code",
      key: "projectCode",
      render: (record: RopeLandingPageData) =>
        record.projectCode && (
          <Link to={`/rope/${record.id}`}>{record.projectCode}</Link>
        ),
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.string(a.projectCode, b.projectCode)
    },
    {
      title: "Project Name",
      key: "projectName",
      onCell: record => {
        if (record.projectCode) {
          return {
            onClick: () => handleDetailsOpenModal(record),
            style: { cursor: "pointer" }
          };
        } else {
          return {};
        }
      },
      render: (record: RopeLandingPageData) =>
        !record.projectCode ? (
          <Link to={`/rope/${record.id}`}>{record.projectName}</Link>
        ) : (
          record.projectName
        ),
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.string(a.projectName, b.projectName)
    },
    {
      title: "Client",
      key: "clientName",
      dataIndex: "clientName",
      onCell: record => {
        return {
          onClick: () => handleDetailsOpenModal(record),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.string(a.clientName, b.clientName)
    },
    {
      title: "Office",
      key: "office",
      dataIndex: "office",
      onCell: record => {
        return {
          onClick: () => handleDetailsOpenModal(record),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.string(a.office, b.office)
    },
    {
      title: "Entity",
      key: "entity",
      dataIndex: "entity",
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.string(a.entity, b.entity)
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: formatDate,
      onCell: record => {
        return {
          onClick: () => handleDetailsOpenModal(record),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.date(a.startDate, b.startDate)
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      onCell: record => {
        return {
          onClick: () => handleDetailsOpenModal(record),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.string(a.status, b.status)
    },
    {
      title: "Version",
      key: "version",
      align: "right",
      dataIndex: "uploadVersion",
      onCell: record => {
        return {
          onClick: () => handleDetailsOpenModal(record),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a: RopeLandingPageData, b: RopeLandingPageData) =>
        sorters.number(a.uploadVersion, b.uploadVersion)
    }
  ];

  function openImportModal(record: RopeLandingPageData) {
    setSelectedRecord(record);
    setIsImportModalVisible(true);
  }

  if (canImportRope) {
    columns.push({
      render: (record: RopeLandingPageData) => (
        <Button
          onClick={() => openImportModal(record)}
          size="small"
          disabled={!RopeStatusImportable.includes(record.status)}
        >
          {record.flags.includes(RopeFlagDynamics) ? (
            <div
              style={{
                float: "left",
                display: "flex",
                alignItems: "center",
                marginTop: "2px"
              }}
            >
              <DynamicsLogo size="30px" />
            </div>
          ) : (
            <div
              style={{
                float: "left",
                display: "flex",
                alignItems: "center",
                marginTop: "-1px"
              }}
            >
              <ProjectorLogo size="18px" />
            </div>
          )}
          Import
        </Button>
      )
    });
  }

  columns = columns.map(column => {
    if (column.key === sortSettings.key) {
      return { ...column, defaultSortOrder: sortSettings.order };
    }
    return column;
  });

  function onChange(_pagination: any, _filters: any, sorter: any) {
    if (Object.keys(sorter).length > 0) {
      setSortSettings({
        key: sorter.columnKey,
        order: sorter.order
      });
    }
  }

  function refresh() {
    setLoading(true);
    getRopeLandingPageDataDto()
      .then(data => {
        const converted = convertRopeLandingPageData(data);
        setRopeData(converted);
        setFilteredRopeData(converted);
        filterRopeLandingPageData(
          {
            onSearch: setFilteredRopeData,
            data: converted,
            settings: filterSettings,
            setSettings: setFilterSettings,
            disabled: loading,
            form: null,
            searchRef: null
          },
          [],
          {
            searchTerm: filterSettings.search,
            selectedStatuses: filterSettings.status,
            selectedClients: filterSettings.client,
            selectedOffices: filterSettings.office,
            selectedEntities: filterSettings.entity,
            selectedProjectCodes: filterSettings.projectCode,
            showInactive: filterSettings.inactive
          }
        );
      })
      .catch(displayErrorNotification)
      .then(() => {
        setLoading(false);
        if (
          searchRef.current !== undefined &&
          searchRef.current.input !== null
        ) {
          searchRef.current.focus();
        }
      });
  }

  return (
    <Stack>
      <Typography.Title>
        Role, Pricing, and Estimation
        <ButtonRow style={{ float: "right" }}>
          <Button
            icon={"upload"}
            disabled={loading || !canUploadRope}
            onClick={() => setIsUploadModalVisible(true)}
          >
            Upload
          </Button>
          <Button
            icon={"download"}
            disabled={loading}
            onClick={() =>
              openInNewTab(
                "https://oneomnicom.sharepoint.com/sites/Credera-OurServices/SitePages/ROPE.aspx?ga=1"
              )
            }
          >
            Download Template
          </Button>
          <Button
            icon={"warning"}
            disabled={loading}
            onClick={() => setIsViewLogVisible(true)}
          >
            View Log
          </Button>
          <SavePreferencesButton
            disabled={loading}
            keys={[
              "RopeLandingPage_FilterSettings",
              "RopeLandingPage_SortSettings"
            ]}
          />
        </ButtonRow>
      </Typography.Title>
      <Toolbar>
        <ROPEProjectFilters
          onSearch={setFilteredRopeData}
          data={ropeData}
          settings={filterSettings}
          setSettings={setFilterSettings}
          disabled={loading}
          searchRef={searchRef}
        />
        <div>
          <span style={{ margin: "0 10px" }}>
            Showing {filteredRopeData.length} of {ropeData.length} Projects
          </span>
        </div>
      </Toolbar>
      <Table
        pagination={{ pageSize: 25, position: "top" }}
        rowKey="id"
        columns={columns}
        dataSource={filteredRopeData}
        loading={loading}
        onChange={onChange}
      />
      {isDetailsModalHidden ? null : (
        <RopeDetailsModal
          modalState={true}
          closeModal={() => setIsDetailsModalHidden(true)}
          record={selectedRecord || null}
        />
      )}
      {isUploadModalVisible && (
        <RopeUploadModal
          modalState={isUploadModalVisible}
          refresh={refresh}
          setLoading={setLoading}
          closeModal={() => setIsUploadModalVisible(false)}
          onUploadSuccess={response => {
            console.log("Upload successful:", response);
          }}
        />
      )}
      {isImportModalVisible && (
        <RopeImportConfirmModal
          modalState={isImportModalVisible}
          closeModal={() => setIsImportModalVisible(false)}
          refresh={() => {
            refresh();
          }}
          record={selectedRecord || null}
        />
      )}
      {isViewLogVisible && (
        <RopeViewLogModal
          modalState={isViewLogVisible}
          closeModal={() => setIsViewLogVisible(false)}
        />
      )}
    </Stack>
  );
};

export default trackComponent(RopeLandingPage, "ROPE Landing Page");
